import { zodResolver } from "@hookform/resolvers/zod";
import { type Dispatch, type FC, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";

import { PosName, PosQrPattern } from "@/api/admin/posIntegration/types";
import {
  CustomCheckbox,
  CustomLabel,
  type ItemsListType,
  Switch,
} from "@/components/atoms";
import { InputControl, SelectControl } from "@/components/molecules";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { cn } from "@/lib/utils";

import {
  type BranchPos,
  BranchPosSchema,
  POS_NAME_LABELS,
  POS_WITHOUT_RAW_TYPE,
  PosIntegratorList,
  TableDigitsList,
} from "../types";
import { normalizePosIntegrator } from "../utils/posIntegrationMapping";
import TableIdMappingField from "./TableIdMappingField";

export const POS_INTEGRATION_TYPES = Object.values(PosName)
  .filter(id => id !== PosName.UNKNOWN)
  .map(id => ({
    id,
    name: POS_NAME_LABELS[id],
  }));

interface Props {
  id: string;
  branchId: string;
  onValidityChange: Dispatch<React.SetStateAction<boolean>>;
  defaultValues: BranchPos;
  onSubmit: (payload: BranchPos, updatedAt?: string) => void;
  hasPosIntegration: boolean;
  /** updatedAt from the integration this screen loaded. Sent back unchanged. */
  updatedAt?: string;
}

const PosIntegration: FC<Props> = ({
  onSubmit,
  defaultValues,
  id,
  branchId,
  onValidityChange,
  hasPosIntegration,
  updatedAt,
}) => {
  const loadedUpdatedAt = useRef<string | undefined>(updatedAt);
  if (loadedUpdatedAt.current === undefined && updatedAt !== undefined) {
    loadedUpdatedAt.current = updatedAt;
  }

  const form = useForm<BranchPos>({
    resolver: zodResolver(BranchPosSchema),
    defaultValues: {
      isPosIntegrated: false,
      tableNumberLength: "3",
      qrScannerEnabled: false,
      qrPattern: "",
      isSettlementEnabled: false,
      posTenderId: "",
      posDiscountId: "",
      autoClose: false,
      isBillLookupEnabled: false,
      isTableNumberLookupEnabled: false,
      isTableIdLookupEnabled: false,
      tableIdMappingFileName: null,
      tableIdMappingRemoved: false,
      tableIdMappingFile: null,
      ...defaultValues,
      integrator: normalizePosIntegrator(defaultValues?.integrator),
    },
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
    setValue,
    getValues,
  } = form;

  const isIntegrated = watch("isPosIntegrated");
  const selectedPos = watch("posName") as PosName;
  const posRawType = watch("posRawType");
  const qrScannerEnabled = watch("qrScannerEnabled");
  const isTableIdLookupEnabled = watch("isTableIdLookupEnabled");
  // Settings of a switched-off POS are only saved onto an existing integration.
  const isLookupFrozen = !hasPosIntegration && !isIntegrated;

  const posProviderItems = useMemo((): ItemsListType => {
    const items: ItemsListType = [...POS_INTEGRATION_TYPES];
    if (selectedPos === PosName.UNKNOWN || posRawType) {
      const rawLabel = posRawType?.trim() || "unspecified";
      if (!items.some(i => "id" in i && i.id === PosName.UNKNOWN)) {
        items.push({
          id: PosName.UNKNOWN,
          name: `Unknown (${rawLabel})`,
        });
      }
    }
    return items;
  }, [selectedPos, posRawType]);

  useEffect(() => {
    if (POS_WITHOUT_RAW_TYPE.includes(selectedPos) && posRawType) {
      setValue("posRawType", undefined, { shouldValidate: true });
    }
  }, [selectedPos, posRawType, setValue]);

  // Pattern layout is unused by mobile; keep a default when scanner is on
  // so the API can still derive isScannerEnabled from qrPattern.
  useEffect(() => {
    if (!qrScannerEnabled) {
      setValue("qrPattern", "", { shouldValidate: true });
      return;
    }
    if (!getValues("qrPattern")) {
      setValue("qrPattern", PosQrPattern.QR_CENTER, { shouldValidate: true });
    }
  }, [qrScannerEnabled, setValue, getValues]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    if (getValues("tableIdMappingFile") || getValues("tableIdMappingRemoved")) {
      return;
    }
    const savedFileName = defaultValues.tableIdMappingFileName ?? null;
    if (getValues("tableIdMappingFileName") === savedFileName) {
      return;
    }
    setValue("tableIdMappingFileName", savedFileName, {
      shouldValidate: true,
    });
  }, [defaultValues.tableIdMappingFileName, getValues, setValue]);

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(data => onSubmit(data, loadedUpdatedAt.current))}
        data-testid="group-information-form"
        id={id}
      >
        <div className="flex flex-col">
          {/* ── Bill Lookup Section ── */}
          <fieldset className="border border-gray-200 rounded-md p-4 mb-5.5">
            <legend className="px-2 text-sm font-semibold">Bill Lookup</legend>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {/* Lookup Method checkboxes */}
              <div className="flex flex-col gap-2">
                <CustomLabel htmlFor="lookupMethod" label="Lookup Method" />
                <div className="flex items-center gap-4">
                  <FormField
                    control={control}
                    name="isBillLookupEnabled"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-1.5">
                        <FormControl>
                          <CustomCheckbox
                            id="isBillLookupEnabled"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isLookupFrozen}
                            data-testid="bill-lookup-checkbox"
                          />
                        </FormControl>
                        <label
                          htmlFor="isBillLookupEnabled"
                          className={cn(
                            "text-sm cursor-pointer",
                            isLookupFrozen && "cursor-not-allowed opacity-50"
                          )}
                        >
                          Bill #
                        </label>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="isTableNumberLookupEnabled"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-1.5">
                        <FormControl>
                          <CustomCheckbox
                            id="isTableNumberLookupEnabled"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isLookupFrozen}
                            data-testid="table-number-lookup-checkbox"
                          />
                        </FormControl>
                        <label
                          htmlFor="isTableNumberLookupEnabled"
                          className={cn(
                            "text-sm cursor-pointer",
                            isLookupFrozen && "cursor-not-allowed opacity-50"
                          )}
                        >
                          Table #
                        </label>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="isTableIdLookupEnabled"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-1.5">
                        <FormControl>
                          <CustomCheckbox
                            id="isTableIdLookupEnabled"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isLookupFrozen}
                            data-testid="table-id-lookup-checkbox"
                          />
                        </FormControl>
                        <label
                          htmlFor="isTableIdLookupEnabled"
                          className={cn(
                            "text-sm cursor-pointer",
                            isLookupFrozen && "cursor-not-allowed opacity-50"
                          )}
                        >
                          Table ID
                        </label>
                      </FormItem>
                    )}
                  />
                </div>
                {isTableIdLookupEnabled && (
                  <TableIdMappingField
                    form={form}
                    branchId={branchId}
                    disabled={isLookupFrozen}
                  />
                )}
              </div>

              {/* Max Table Number Digits */}
              <div className="flex flex-col gap-1">
                <CustomLabel
                  htmlFor="tableNumberDigits"
                  label="Max Table Number Digits"
                />
                <SelectControl<BranchPos>
                  form={form}
                  nameSelect="tableNumberLength"
                  placeholder="Select"
                  itemsList={TableDigitsList}
                  triggerProps={{
                    "data-testid": "pos-tableNumberDigits",
                  }}
                  classes="mb-0!"
                />
              </div>
            </div>

            <div className="mt-4">
              <FormField
                control={control}
                name="qrScannerEnabled"
                render={({ field }) => (
                  <FormItem className="flex items-center">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="qr-scanner-switch"
                      />
                    </FormControl>
                    <CustomLabel
                      htmlFor="qrScannerEnabled"
                      label="QR scanner"
                    />
                  </FormItem>
                )}
              />
            </div>
          </fieldset>

          {/* ── Integration Details Section ── */}
          <fieldset className="border border-gray-200 rounded-md p-4">
            <legend className="px-2 text-sm font-semibold">
              Integration Details
            </legend>

            <FormField
              control={control}
              name="isPosIntegrated"
              render={({ field }) => (
                <FormItem className="flex items-center mb-5.5">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="pos-integrated-switch"
                    />
                  </FormControl>
                  <CustomLabel
                    htmlFor="isPosIntegrated"
                    label="POS integrated"
                  />
                </FormItem>
              )}
            />

            {/*
              Use hidden instead of conditional rendering so form values
              survive toggle off/on.
            */}
            <div className={cn(!isIntegrated && "hidden")}>
              {/* POS Name + Integrator side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mb-5.5">
                <div className="flex flex-col gap-1">
                  <CustomLabel htmlFor="posName" label="POS Name" />
                  <SelectControl<BranchPos>
                    form={form}
                    nameSelect="posName"
                    placeholder="Select"
                    itemsList={posProviderItems}
                    triggerProps={{
                      "data-testid": "pos-posName",
                    }}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <CustomLabel htmlFor="integrator" label="Integrator" />
                  <SelectControl<BranchPos>
                    form={form}
                    nameSelect="integrator"
                    placeholder="Select"
                    itemsList={PosIntegratorList}
                    triggerProps={{
                      "data-testid": "pos-integrator",
                    }}
                    classes="mb-0!"
                  />
                </div>
              </div>

              {/* Enable Settlement + Auto Close */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mb-5.5">
                <FormField
                  control={control}
                  name="isSettlementEnabled"
                  render={({ field }) => (
                    <FormItem className="flex items-center">
                      <FormControl>
                        <Switch
                          checked={Boolean(field.value)}
                          onCheckedChange={field.onChange}
                          data-testid="isSettlementEnabled-switch"
                        />
                      </FormControl>
                      <CustomLabel
                        htmlFor="isSettlementEnabled"
                        label="Enable Settlement"
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="autoClose"
                  render={({ field }) => (
                    <FormItem className="flex items-center">
                      <FormControl>
                        <Switch
                          checked={Boolean(field.value)}
                          onCheckedChange={field.onChange}
                          data-testid="isAutoCloseEnabled-switch"
                        />
                      </FormControl>
                      <CustomLabel htmlFor="autoClose" label="Auto Close" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Base URL, POS Client ID, POS Branch ID */}
              <InputControl<BranchPos>
                formControl={control}
                label="Base URL"
                nameField="baseUrl"
                className="w-full"
                data-testid="pos-baseUrl-input"
              />
              <InputControl<BranchPos>
                formControl={control}
                label="POS Client ID"
                nameField="posClientId"
                className="w-full"
                data-testid="posClientId-input"
              />
              <InputControl<BranchPos>
                formControl={control}
                label="POS Branch ID"
                nameField="posBranchId"
                className="w-full"
                data-testid="posBranchId-input"
              />

              {/* Tender ID + Discount ID side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <InputControl<BranchPos>
                  formControl={control}
                  label="Tender ID"
                  nameField="posTenderId"
                  className="w-full"
                  data-testid="posTenderId-input"
                />
                <InputControl<BranchPos>
                  formControl={control}
                  label="Discount ID"
                  nameField="posDiscountId"
                  className="w-full"
                  data-testid="posDiscountId-input"
                />
              </div>
            </div>
          </fieldset>
        </div>
      </form>
    </Form>
  );
};

export default PosIntegration;
